import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLogin, useNotify } from 'react-admin';
import { Field, Form } from 'react-final-form';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CardActions, CircularProgress, TextField } from '@material-ui/core';
import TwoFactorVerification from '../components/TwoFactorVerification';
import PasswordVisibilityInput from './passwordInput';

const useStyles = makeStyles(theme => ({
    form: {
        padding: '0 1em 1em 1em',
    },
    input: {
        marginTop: '1em',
    },
    button: {
        width: '100%',
    },
    icon: {
        marginRight: theme.spacing(1),
    },
}));

const Input = ({
    meta: { touched, error },
    input: inputProps,
    ...props
}) => (
    <TextField
        error={!!(touched && error)}
        helperText={touched && error}
        {...inputProps}
        {...props}
        fullWidth
    />
);

const PasswordInput = ({
    meta: { touched, error },
    input: inputProps,
    ...props
}) => (
    <PasswordVisibilityInput
        error={!!(touched && error)}
        helperText={touched && error}
        {...inputProps}
        {...props}
    />
);

const CustomLoginForm = () => {
    const [loading, setLoading] = useState(false);
    const [showTwoFactor, setShowTwoFactor] = useState(false);
    const [username, setUsername] = useState('');
    const mounted = useRef(true);
    const classes = useStyles();
    const login = useLogin();
    const notify = useNotify();

    useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);

    const safeSetState = useCallback((setter) => {
        if (mounted.current) {
            setter();
        }
    }, []);

    const validate = (values) => {
        const errors = {};
        if (!values.username) {
            errors.username = 'Required';
        }
        if (!values.password) {
            errors.password = 'Required';
        }
        return errors;
    };

    const handleSubmit = useCallback(async (auth) => {
        safeSetState(() => setLoading(true));
        try {
            // First, try to authenticate with the backend directly
            const response = await fetch(`${localStorage.getItem('domain')}/api/users/authenticate`, {
                method: 'POST',
                body: JSON.stringify(auth),
                headers: new Headers({ 
                    'Content-Type': 'application/json',
                    'X-COMPANY': 'ROOT',
                    'Bypass-Tunnel-Reminder': 'true' 
                }),
            });

            if (!response.ok) {
                throw new Error(response.statusText);
            }

            const data = await response.json();

            if (data.requiresTwoFactor) {
                safeSetState(() => {
                    setUsername(auth.username);
                    setShowTwoFactor(true);
                });
            } else {
                // If no 2FA required, use login to set up the session
                await login({ 
                    token: data.token, 
                    roles: data.roles,
                    requiresTwoFactor: false // Explicitly set to false when 2FA is not required
                });
            }
        } catch (error) {
            if (mounted.current) {
                notify(
                    typeof error === 'string'
                        ? error
                        : typeof error === 'undefined' || !error.message
                        ? 'ra.auth.sign_in_error'
                        : error.message,
                    'warning'
                );
            }
        } finally {
            safeSetState(() => setLoading(false));
        }
    }, [login, notify, safeSetState]);

    const handleVerificationSuccess = useCallback(async (data) => {
        try {
            // After successful 2FA verification, we know 2FA is enabled
            await login({ 
                token: data.token, 
                roles: data.roles,
                requiresTwoFactor: true // We know it's true since we just completed 2FA
            });
        } catch (error) {
            notify('Error setting up session', 'error');
        }
        safeSetState(() => setShowTwoFactor(false));
    }, [login, notify, safeSetState]);

    const handleVerificationCancel = useCallback(() => {
        safeSetState(() => {
            setShowTwoFactor(false);
            setUsername('');
        });
    }, [safeSetState]);

    return (
        <>
            <Form
                onSubmit={handleSubmit}
                validate={validate}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} className={classes.form}>
                        <div className={classes.input}>
                            <Field
                                name="username"
                                component={Input}
                                label="Username"
                                disabled={loading || showTwoFactor}
                            />
                        </div>
                        <div className={classes.input}>
                            <Field
                                name="password"
                                component={PasswordInput}
                                label="Password"
                                disabled={loading || showTwoFactor}
                            />
                        </div>
                        <CardActions>
                            <Button
                                variant="contained"
                                type="submit"
                                color="primary"
                                disabled={loading || showTwoFactor}
                                className={classes.button}
                            >
                                {loading && (
                                    <CircularProgress
                                        className={classes.icon}
                                        size={18}
                                        thickness={2}
                                    />
                                )}
                                Login
                            </Button>
                        </CardActions>
                    </form>
                )}
            />
            <TwoFactorVerification
                open={showTwoFactor}
                username={username}
                onVerify={handleVerificationSuccess}
                onClose={handleVerificationCancel}
            />
        </>
    );
};

export default CustomLoginForm; 